import { Component, Inject, computed } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideAngularModule, ChartColumnIncreasing, X, ClipboardCopy } from 'lucide-angular';
import { Message, ProcessMessage } from '../../../core/models/message.model';
import { TraceFormatterService } from '../../../core/services/trace-formatter.service';
import { copyToClipboard } from '../../../core/utils/utils';

interface AgentTraceEntry {
  type?: string;
  body?: {
    tool_name?: string;
  };
}

type BenchmarkTraceStepType = 'thinking' | 'tool' | 'result' | 'answer';

interface BenchmarkTraceStep {
  label: string;
  type: BenchmarkTraceStepType;
  className: string;
}

interface ConversationBenchmarkRow {
  turn: number;
  fullUserMessage: string;
  fullBotMessage: string;
  userMessage: string;
  botMessage: string;
  responseTimeSeconds: number;
  traceSummary: string;
  traceSteps: BenchmarkTraceStep[];
}

interface BenchmarkTraceSummary {
  summary: string;
  steps: BenchmarkTraceStep[];
}

export interface SessionBenchmarkDialogData {
  sessionId: string;
  conversationId?: string;
  conversationStartedAt?: string | Date;
  allMessages: Message[];
  liveProcessTraces?: ProcessMessage[];
}

@Component({
  selector: 'app-session-benchmark-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatSnackBarModule,
    MatTooltipModule,
    LucideAngularModule
  ],
  templateUrl: './session-benchmark-dialog.component.html',
  styleUrls: ['./session-benchmark-dialog.component.scss']
})
export class SessionBenchmarkDialogComponent {
  readonly BenchmarkIcon = ChartColumnIncreasing;
  readonly CloseIcon = X;
  readonly CopyIcon = ClipboardCopy;

  benchmarkRows = computed<ConversationBenchmarkRow[]>(() => {
    return this.buildBenchmarkRows(this.data.allMessages ?? []);
  });

  totalBenchmarkSeconds = computed<number>(() => {
    return this.benchmarkRows().reduce((sum, row) => sum + row.responseTimeSeconds, 0);
  });

  averageBenchmarkSeconds = computed<number>(() => {
    const rows = this.benchmarkRows();
    if (rows.length === 0) {
      return 0;
    }

    return this.totalBenchmarkSeconds() / rows.length;
  });

  minBenchmarkSeconds = computed<number>(() => {
    const rows = this.benchmarkRows();
    if (rows.length === 0) {
      return 0;
    }

    return Math.min(...rows.map(row => row.responseTimeSeconds));
  });

  maxBenchmarkSeconds = computed<number>(() => {
    const rows = this.benchmarkRows();
    if (rows.length === 0) {
      return 0;
    }

    return Math.max(...rows.map(row => row.responseTimeSeconds));
  });

  constructor(
    public dialogRef: MatDialogRef<SessionBenchmarkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SessionBenchmarkDialogData,
    private traceFormatter: TraceFormatterService,
    private snackBar: MatSnackBar
  ) {}

  close(): void {
    this.dialogRef.close();
  }

  formatSeconds(value: number): string {
    return value.toFixed(2);
  }

  getConversationIdForDisplay(): string {
    const conversationId = this.data.conversationId?.trim();
    return conversationId || '-';
  }

  getConversationDateForDisplay(): string {
    const conversationDate = this.getConversationDate();
    if (!conversationDate) {
      return '-';
    }

    return this.formatFullDate(conversationDate);
  }

  async copyBenchmarkToClipboard(): Promise<void> {
    const rows = this.benchmarkRows();

    if (rows.length === 0) {
      this.snackBar.open('No hay datos para copiar', 'Cerrar', {
        duration: 3000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    const tsvContent = this.buildSheetTsv(rows);
    const copied = await copyToClipboard(tsvContent);

    if (copied) {
      this.snackBar.open('Benchmark copiado. Puedes pegarlo en Google Sheets.', 'Cerrar', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
      return;
    }

    this.snackBar.open('No se pudo copiar el benchmark', 'Cerrar', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  async copyBenchmarkValuesToClipboard(): Promise<void> {
    const rows = this.benchmarkRows();

    if (rows.length === 0) {
      this.snackBar.open('No hay datos para copiar', 'Cerrar', {
        duration: 3000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    const tsvContent = this.buildValuesOnlySheetTsv(rows);
    const copied = await copyToClipboard(tsvContent);

    if (copied) {
      this.snackBar.open('Benchmark (solo info) copiado para Google Sheets.', 'Cerrar', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
      return;
    }

    this.snackBar.open('No se pudo copiar el benchmark', 'Cerrar', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  private buildSheetTsv(rows: ConversationBenchmarkRow[]): string {
    const sections = [
      ['Session ID', this.data.sessionId],
      ['ConversationID', this.getConversationIdForDisplay()],
      ['Fecha', this.getConversationDateForDisplay()],
      ['Transcripción', this.buildTranscriptForExport(this.data.allMessages ?? [])],
      ['Total (s)', this.formatSecondsForExport(this.totalBenchmarkSeconds())],
      ['Promedio (s)', this.formatSecondsForExport(this.averageBenchmarkSeconds())],
      ['Mínimo (s)', this.formatSecondsForExport(this.minBenchmarkSeconds())],
      ['Máximo (s)', this.formatSecondsForExport(this.maxBenchmarkSeconds())],
      ['Tiempos turno', ''],
      ...rows.map(row => [
        row.turn.toString(),
        this.formatSecondsForExport(row.responseTimeSeconds)
      ])
    ];

    return sections
      .map(cols => cols.map(value => this.sanitizeTsvValue(value)).join('\t'))
      .join('\n');
  }

  private buildValuesOnlySheetTsv(rows: ConversationBenchmarkRow[]): string {
    const values = [
      this.data.sessionId,
      this.getConversationIdForDisplay(),
      this.getConversationDateForDisplay(),
      this.buildTranscriptForExport(this.data.allMessages ?? []),
      this.formatSecondsForExport(this.totalBenchmarkSeconds()),
      this.formatSecondsForExport(this.averageBenchmarkSeconds()),
      this.formatSecondsForExport(this.minBenchmarkSeconds()),
      this.formatSecondsForExport(this.maxBenchmarkSeconds()),
      ...rows.map(row => this.formatSecondsForExport(row.responseTimeSeconds))
    ];

    return values
      .map(value => this.sanitizeTsvValue(value))
      .join('\n');
  }

  private sanitizeTsvValue(value: string): string {
    const sanitized = value
      .replace(/\t/g, ' ')
      .replace(/\r\n?/g, '\n')
      .trim();

    const escaped = sanitized.replace(/"/g, '""');

    if (escaped.includes('\n') || escaped.includes('"')) {
      return `"${escaped}"`;
    }

    return escaped;
  }

  private formatSecondsForExport(value: number): string {
    return `=${this.formatSeconds(value)}`;
  }

  private buildTranscriptForExport(allMessages: Message[]): string {
    const orderedMessages = [...allMessages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    const transcriptParts = orderedMessages
      .filter(message => message.state !== 'DISCARDED' && this.hasMessageContent(message.content))
      .map(message => {
        const role = message.isUser ? 'user' : 'bot';
        const normalizedMessage = this.normalizeMessage(message.content);
        return `[${this.formatDayHour(message.timestamp)}] ${role}: ${normalizedMessage}`;
      });

    if (transcriptParts.length === 0) {
      return '-';
    }

    return transcriptParts.join('\n');
  }

  private getConversationDate(): Date | null {
    const explicitDate = this.parseConversationDate(this.data.conversationStartedAt);
    if (explicitDate) {
      return explicitDate;
    }

    const orderedMessages = [...(this.data.allMessages ?? [])]
      .filter(message => message.timestamp instanceof Date && !Number.isNaN(message.timestamp.getTime()))
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    return orderedMessages[0]?.timestamp ?? null;
  }

  private parseConversationDate(value?: string | Date): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const numericValue = Number(trimmedValue);
    if (Number.isFinite(numericValue)) {
      const millis = numericValue > 1e12 ? numericValue : numericValue * 1000;
      const numericDate = new Date(millis);
      if (!Number.isNaN(numericDate.getTime())) {
        return numericDate;
      }
    }

    const parsedDate = new Date(trimmedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private formatFullDate(date: Date): string {
    const day = this.padNumber(date.getDate());
    const month = this.padNumber(date.getMonth() + 1);
    const year = date.getFullYear();
    const hour = this.padNumber(date.getHours());
    const minute = this.padNumber(date.getMinutes());
    const second = this.padNumber(date.getSeconds());

    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  }

  private formatDayHour(date: Date): string {
    const day = this.padNumber(date.getDate());
    const month = this.padNumber(date.getMonth() + 1);
    const hour = this.padNumber(date.getHours());
    const minute = this.padNumber(date.getMinutes());

    return `${day}/${month} ${hour}:${minute}`;
  }

  private padNumber(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private buildBenchmarkRows(allMessages: Message[]): ConversationBenchmarkRow[] {
    const orderedMessages = [...allMessages].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const userMessages = orderedMessages.filter(message => message.isUser && message.state !== 'DISCARDED');
    const usedBotMessageIds = new Set<string>();
    const rows: ConversationBenchmarkRow[] = [];

    userMessages.forEach((userMessage, userIndex) => {
      const nextUserTimestamp = userMessages[userIndex + 1]?.timestamp.getTime() ?? Number.POSITIVE_INFINITY;
      const botGroup = this.findBotGroup(userMessage, orderedMessages, nextUserTimestamp, usedBotMessageIds);

      if (botGroup.length === 0) {
        return;
      }

      const finalBotMessage = this.getFinalBotMessage(botGroup);
      if (!finalBotMessage) {
        return;
      }

      const fullUserMessage = this.normalizeMessage(userMessage.content);
      const fullBotMessage = this.normalizeMessage(finalBotMessage.content);
      const responseStartUserMessage = this.getResponseStartUserMessage(
        userMessage,
        finalBotMessage,
        orderedMessages
      );

      const responseTimeSeconds = Math.max(
        0,
        (finalBotMessage.timestamp.getTime() - responseStartUserMessage.timestamp.getTime()) / 1000
      );

      const traceSummary = this.buildTraceSummary(botGroup, userMessage.id);

      rows.push({
        turn: rows.length + 1,
        fullUserMessage,
        fullBotMessage,
        userMessage: this.truncateMessage(fullUserMessage),
        botMessage: this.truncateMessage(fullBotMessage),
        responseTimeSeconds,
        traceSummary: traceSummary.summary,
        traceSteps: traceSummary.steps
      });
    });

    return rows;
  }

  private findBotGroup(
    userMessage: Message,
    orderedMessages: Message[],
    nextUserTimestamp: number,
    usedBotMessageIds: Set<string>
  ): Message[] {
    const answeredResponses = orderedMessages
      .filter(message =>
        !message.isUser &&
        message.state !== 'DISCARDED' &&
        message.answeredMessageId === userMessage.id &&
        !usedBotMessageIds.has(message.id)
      )
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    if (answeredResponses.length > 0) {
      answeredResponses.forEach(response => usedBotMessageIds.add(response.id));
      return answeredResponses;
    }

    const fallbackResponses = orderedMessages
      .filter(message =>
        !message.isUser &&
        message.state !== 'DISCARDED' &&
        !usedBotMessageIds.has(message.id) &&
        message.timestamp.getTime() >= userMessage.timestamp.getTime() &&
        message.timestamp.getTime() < nextUserTimestamp
      )
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    fallbackResponses.forEach(response => usedBotMessageIds.add(response.id));
    return fallbackResponses;
  }

  private getResponseStartUserMessage(
    anchorUserMessage: Message,
    finalBotMessage: Message,
    orderedMessages: Message[]
  ): Message {
    const startTimestamp = anchorUserMessage.timestamp.getTime();
    const endTimestamp = finalBotMessage.timestamp.getTime();

    const candidateUsers = orderedMessages
      .filter(message =>
        message.isUser &&
        message.timestamp.getTime() >= startTimestamp &&
        message.timestamp.getTime() <= endTimestamp
      )
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    return candidateUsers[candidateUsers.length - 1] ?? anchorUserMessage;
  }

  private getFinalBotMessage(botGroup: Message[]): Message | null {
    const orderedGroup = [...botGroup].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const visibleMessages = orderedGroup.filter(message => message.state !== 'DISCARDED');

    if (visibleMessages.length > 0) {
      return visibleMessages[visibleMessages.length - 1];
    }

    return orderedGroup.length > 0 ? orderedGroup[orderedGroup.length - 1] : null;
  }

  private buildTraceSummary(botGroup: Message[], userMessageId?: string): BenchmarkTraceSummary {
    const orderedBotGroup = [...botGroup].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const orderedSteps = this.getStoredTraceSteps(orderedBotGroup);

    if (orderedSteps.length === 0 && userMessageId) {
      const liveSteps = this.getLiveTraceSteps(userMessageId);
      orderedSteps.push(...liveSteps);
    }

    this.enrichKnowledgeSteps(orderedSteps, orderedBotGroup);

    const hasAnswerText = orderedBotGroup.some(message => this.hasMessageContent(message.content));
    if (hasAnswerText && !orderedSteps.includes('answer')) {
      orderedSteps.push('answer');
    }

    const normalizedSteps = this.removeConsecutiveDuplicates(orderedSteps);

    if (normalizedSteps.length === 0) {
      if (hasAnswerText) {
        const fallbackSteps = this.toTraceSteps(['answer']);
        return {
          summary: 'answer',
          steps: fallbackSteps
        };
      }

      return {
        summary: '-',
        steps: []
      };
    }

    return {
      summary: this.formatStepSequence(normalizedSteps),
      steps: this.toTraceSteps(normalizedSteps)
    };
  }

  private getStoredTraceSteps(botGroup: Message[]): string[] {
    const seenTracePayloads = new Set<string>();
    const steps: string[] = [];

    botGroup.forEach(botMessage => {
      if (!botMessage.agentTrace) {
        return;
      }

      const traceKey = botMessage.agentTrace;
      if (seenTracePayloads.has(traceKey)) {
        return;
      }

      seenTracePayloads.add(traceKey);
      const normalizedTrace = this.traceFormatter.normalizeAgentTrace(botMessage.agentTrace, botMessage.agentName);
      const tokens = this.extractStepsFromNormalizedTrace(normalizedTrace);
      steps.push(...tokens);
    });

    return steps;
  }

  private getLiveTraceSteps(userMessageId: string): string[] {
    const traces = this.getLiveProcessTracesForUser(userMessageId);

    return traces
      .map(trace => this.mapLiveTraceToToken(trace))
      .filter((token): token is string => !!token);
  }

  private getLiveProcessTracesForUser(userMessageId: string): ProcessMessage[] {
    const traces = this.data.liveProcessTraces ?? [];

    return traces
      .filter(trace => trace.type === 'trace' && trace.answeredMessageId === userMessageId)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  private mapLiveTraceToToken(trace: ProcessMessage): string {
    if (trace.traceType === 'thinking') {
      return 'thinking';
    }

    if (trace.traceType === 'tool_use') {
      const toolName = this.extractLiveToolName(trace);
      return this.normalizeToolName(toolName) || 'tool';
    }

    if (trace.traceType === 'tool_output') {
      return 'result';
    }

    if (trace.traceType === 'final_answer') {
      return 'answer';
    }

    return '';
  }

  private extractLiveToolName(trace: ProcessMessage): string {
    const dataToolName = typeof trace.data?.tool_name === 'string' ? trace.data.tool_name : '';
    if (dataToolName) {
      return dataToolName;
    }

    const content = trace.content || '';
    const toolMatch = content.match(/Herramienta:\s*([^\n]+)/i);
    if (toolMatch?.[1]) {
      return toolMatch[1].trim();
    }

    return '';
  }

  private extractStepsFromNormalizedTrace(normalizedTrace?: any[]): string[] {
    if (!normalizedTrace || !Array.isArray(normalizedTrace)) {
      return [];
    }

    const steps: string[] = [];

    normalizedTrace.forEach(agentGroup => {
      const traces = Array.isArray(agentGroup?.traces) ? agentGroup.traces : [];
      traces.forEach((trace: AgentTraceEntry) => {
        const token = this.mapTraceToToken(trace);
        if (token) {
          steps.push(token);
        }
      });
    });

    return steps;
  }

  private mapTraceToToken(trace: AgentTraceEntry): string {
    const traceType = trace.type?.toLowerCase();

    if (traceType === 'thinking') {
      return 'thinking';
    }

    if (traceType === 'tool_use') {
      const normalizedToolName = this.normalizeToolName(trace.body?.tool_name);
      return normalizedToolName || 'tool';
    }

    if (traceType === 'tool_output') {
      return 'result';
    }

    if (traceType === 'final_answer') {
      return 'answer';
    }

    return '';
  }

  private normalizeToolName(toolName?: string): string {
    if (!toolName) {
      return '';
    }

    const normalized = toolName.trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    if (['retrieve_knowledge', 'retrieve_from_knowledge_base', 'kb'].includes(normalized)) {
      return 'kb';
    }

    return normalized.replace(/\s+/g, '_');
  }

  private hasMessageContent(content?: string): boolean {
    if (!content) {
      return false;
    }

    return content.replace(/\s+/g, ' ').trim().length > 0;
  }

  private enrichKnowledgeSteps(steps: string[], botGroup: Message[]): void {
    if (!this.hasRetrievedArticles(botGroup)) {
      return;
    }

    this.ensureStepExistsBeforeBoundary(steps, 'kb', ['result', 'answer']);
    this.ensureStepExistsBeforeBoundary(steps, 'result', ['answer']);
  }

  private hasRetrievedArticles(botGroup: Message[]): boolean {
    return botGroup.some(message => (message.retrievedArticles?.length ?? 0) > 0);
  }

  private ensureStepExistsBeforeBoundary(
    steps: string[],
    step: string,
    boundaries: string[]
  ): void {
    if (steps.includes(step)) {
      return;
    }

    const insertIndex = this.findFirstBoundaryIndex(steps, boundaries);
    if (insertIndex >= 0) {
      steps.splice(insertIndex, 0, step);
      return;
    }

    steps.push(step);
  }

  private findFirstBoundaryIndex(steps: string[], boundaries: string[]): number {
    for (const boundary of boundaries) {
      const index = steps.indexOf(boundary);
      if (index >= 0) {
        return index;
      }
    }

    return -1;
  }

  private removeConsecutiveDuplicates(steps: string[]): string[] {
    return steps.filter((step, index) => index === 0 || steps[index - 1] !== step);
  }

  private toTraceSteps(steps: string[]): BenchmarkTraceStep[] {
    return steps.map(step => {
      const stepType = this.getTraceStepType(step);

      return {
        label: step,
        type: stepType,
        className: this.getTraceStepClass(stepType)
      };
    });
  }

  private getTraceStepType(step: string): BenchmarkTraceStepType {
    if (step === 'thinking') {
      return 'thinking';
    }

    if (step === 'result') {
      return 'result';
    }

    if (step === 'answer') {
      return 'answer';
    }

    return 'tool';
  }

  private getTraceStepClass(stepType: BenchmarkTraceStepType): string {
    const baseClasses = 'text-xs font-semibold';

    if (stepType === 'thinking') {
      return `${baseClasses} text-purple-600 dark:text-purple-400`;
    }

    if (stepType === 'tool') {
      return `${baseClasses} text-green-600 dark:text-green-400`;
    }

    if (stepType === 'result') {
      return `${baseClasses} text-yellow-600 dark:text-yellow-400`;
    }

    return `${baseClasses} text-blue-600 dark:text-blue-400`;
  }

  private formatStepSequence(steps: string[]): string {
    const separatorTokens = new Set(['result', 'answer']);
    const segments: string[] = [];
    let currentSegment: string[] = [];

    steps.forEach(step => {
      if (separatorTokens.has(step)) {
        if (currentSegment.length > 0) {
          segments.push(currentSegment.join(' + '));
          currentSegment = [];
        }
        segments.push(step);
        return;
      }

      currentSegment.push(step);
    });

    if (currentSegment.length > 0) {
      segments.push(currentSegment.join(' + '));
    }

    return segments.join(' → ');
  }

  private normalizeMessage(content?: string): string {
    if (!content) {
      return '-';
    }

    const normalized = content.replace(/\s+/g, ' ').trim();
    return normalized || '-';
  }

  private truncateMessage(content: string, maxLength = 160): string {
    if (content.length <= maxLength) {
      return content;
    }

    return `${content.slice(0, maxLength)}...`;
  }
}
